import { useMemo, useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { PivotConfig, ColumnMeta } from '../../types/reportBuilder';
import { FIELD_COLORS } from './fieldEncoding';

function formatCellValue(value: unknown, col?: ColumnMeta): string {
  if (value === null || value === undefined) return '-';
  if (!col) return String(value);
  switch (col.display_format) {
    case 'percentage': return `${Number(value).toFixed(1)}%`;
    case 'currency': return `$${Number(value).toLocaleString()}`;
    case 'decimal_2': return Number(value).toFixed(2);
    default: return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
}

// ─── Pivot Configurator ──────────────────────────────────────────────────────

function PivotConfigurator() {
  const { results, pivotConfig, setPivotConfig, autoDetectPivotConfig } = useReportBuilderStore();

  if (!results) return null;

  const dims = results.columns.filter(c => c.is_dimension || c.is_time_grouping);
  const measures = results.columns.filter(c => c.is_measure);

  if (!pivotConfig) {
    const canAutoPivot = dims.length >= 2 && measures.length >= 1;
    const canManualPivot = dims.length >= 1 && measures.length >= 1;
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
        <p className="text-sm font-medium text-gray-600">Configure Pivot Table</p>
        <p className="mt-1 text-xs text-gray-400">
          {!canManualPivot
            ? 'Pivot requires at least 2 dimensions and 1 measure in your query.'
            : 'Select row, column, and value fields to create a matrix view.'}
        </p>
        <div className="mt-3 flex items-center justify-center gap-2">
          {canAutoPivot && (
            <button onClick={autoDetectPivotConfig}
              className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90">
              Auto-detect
            </button>
          )}
          {canManualPivot && (
            <button onClick={() => {
              setPivotConfig({
                rowField: dims[0].key,
                columnField: dims.length >= 2 ? dims[1].key : dims[0].key,
                valueField: measures[0].key,
                showRowTotals: true,
                showColumnTotals: true,
              });
            }}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100">
              Configure manually
            </button>
          )}
        </div>
      </div>
    );
  }

  const selectClass = 'rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none';

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border bg-gray-50 px-3 py-2">
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-medium text-gray-500">Rows:</span>
        <select value={pivotConfig.rowField}
          onChange={(e) => setPivotConfig({ ...pivotConfig, rowField: e.target.value })}
          className={selectClass}>
          {dims.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-medium text-gray-500">Columns:</span>
        <select value={pivotConfig.columnField}
          onChange={(e) => setPivotConfig({ ...pivotConfig, columnField: e.target.value })}
          className={selectClass}>
          {dims.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-medium text-gray-500">Values:</span>
        <select value={pivotConfig.valueField}
          onChange={(e) => setPivotConfig({ ...pivotConfig, valueField: e.target.value })}
          className={selectClass}>
          {measures.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-1 text-[10px] text-gray-500">
        <input type="checkbox" checked={pivotConfig.showRowTotals}
          onChange={(e) => setPivotConfig({ ...pivotConfig, showRowTotals: e.target.checked })}
          className="h-3 w-3" />
        Row totals
      </label>
      <label className="flex items-center gap-1 text-[10px] text-gray-500">
        <input type="checkbox" checked={pivotConfig.showColumnTotals}
          onChange={(e) => setPivotConfig({ ...pivotConfig, showColumnTotals: e.target.checked })}
          className="h-3 w-3" />
        Column totals
      </label>
    </div>
  );
}

// ─── Pivot Table Grid ────────────────────────────────────────────────────────

function PivotGrid({ config }: { config: PivotConfig }) {
  const { results } = useReportBuilderStore();

  const { uniqueRows, uniqueCols, lookup, rowTotals, colTotals, grandTotal, valueMeta } = useMemo(() => {
    if (!results?.data) return { uniqueRows: [], uniqueCols: [], lookup: new Map(), rowTotals: new Map(), colTotals: new Map(), grandTotal: 0, valueMeta: undefined };

    const rows = new Set<string>();
    const cols = new Set<string>();
    const lkp = new Map<string, number>();
    const rTotals = new Map<string, number>();
    const cTotals = new Map<string, number>();
    let gTotal = 0;

    for (const row of results.data) {
      const rowVal = String(row[config.rowField] ?? '');
      const colVal = String(row[config.columnField] ?? '');
      const val = Number(row[config.valueField] ?? 0);

      rows.add(rowVal);
      cols.add(colVal);
      lkp.set(`${rowVal}|||${colVal}`, val);
      rTotals.set(rowVal, (rTotals.get(rowVal) ?? 0) + val);
      cTotals.set(colVal, (cTotals.get(colVal) ?? 0) + val);
      gTotal += val;
    }

    const vMeta = results.columns.find(c => c.key === config.valueField);

    return {
      uniqueRows: Array.from(rows).sort(),
      uniqueCols: Array.from(cols).sort(),
      lookup: lkp,
      rowTotals: rTotals,
      colTotals: cTotals,
      grandTotal: gTotal,
      valueMeta: vMeta,
    };
  }, [results, config]);

  if (uniqueRows.length === 0 || uniqueCols.length === 0) {
    return <div className="p-4 text-center text-xs text-gray-400">No data for pivot view. Ensure at least 2 dimensions and 1 measure.</div>;
  }

  const rowLabel = results?.columns.find(c => c.key === config.rowField)?.label ?? config.rowField;

  return (
    <div className="overflow-auto rounded-lg border border-gray-200">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50">
            <th className="border-b border-r px-3 py-2 text-left font-medium text-gray-600">{rowLabel}</th>
            {uniqueCols.map(col => (
              <th key={col} className="border-b border-r px-3 py-2 text-right font-medium text-gray-600 whitespace-nowrap">{col || '(empty)'}</th>
            ))}
            {config.showRowTotals && (
              <th className="border-b px-3 py-2 text-right font-semibold text-gray-700 bg-gray-100">Total</th>
            )}
          </tr>
        </thead>
        <tbody>
          {uniqueRows.map(row => (
            <tr key={row} className="hover:bg-gray-50">
              <td className="border-b border-r px-3 py-1.5 font-medium text-gray-700 whitespace-nowrap">{row || '(empty)'}</td>
              {uniqueCols.map(col => {
                const val = lookup.get(`${row}|||${col}`);
                return (
                  <td key={col} className="border-b border-r px-3 py-1.5 text-right tabular-nums">
                    {val !== undefined ? formatCellValue(val, valueMeta) : '-'}
                  </td>
                );
              })}
              {config.showRowTotals && (
                <td className="border-b px-3 py-1.5 text-right font-semibold bg-gray-50 tabular-nums">
                  {formatCellValue(rowTotals.get(row) ?? 0, valueMeta)}
                </td>
              )}
            </tr>
          ))}
          {config.showColumnTotals && (
            <tr className="bg-gray-100 font-semibold">
              <td className="border-r px-3 py-2 text-gray-700">Total</td>
              {uniqueCols.map(col => (
                <td key={col} className="border-r px-3 py-2 text-right tabular-nums">
                  {formatCellValue(colTotals.get(col) ?? 0, valueMeta)}
                </td>
              ))}
              {config.showRowTotals && (
                <td className="px-3 py-2 text-right tabular-nums">{formatCellValue(grandTotal, valueMeta)}</td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Pivot View ─────────────────────────────────────────────────────────

export default function PivotView() {
  const { pivotConfig } = useReportBuilderStore();

  return (
    <div className="space-y-3">
      <PivotConfigurator />
      {pivotConfig && <PivotGrid config={pivotConfig} />}
    </div>
  );
}
